import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Button } from 'antd';
import { LoadingPopup } from './loadingPopup/LoadingPopup';
import { PaginationController } from '../paginationController';

import './styles.less';
import LearningList from '../learningList';
import { Translate } from '../../../services';
import CaretIcon from '../caretIcon';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { breakpointSpecs } from '../learningList/config';

export const LearningListController = ( { className, loading, data } ) => {

    const [ activePage, setActivePage ] = useState( 0 );
    const handlePageChange = useCallback( ( pageIndex, _pageCount ) => {
        setActivePage( pageIndex );
    }, [ setActivePage ] );

    const [ paginationController, setPaginationController ] = useState( undefined );
    const breakpoint = useBreakpoint();
    
    const itemsPerPage = useMemo( () => {
        const vals = Object.keys( breakpoint ).filter( key => breakpoint[ key ] ).map( key => breakpointSpecs[ key ] * 2 );
        return Math.max( 4, ...vals  );
    }, [ breakpoint ] );

    /**
     * Returns the user back to the first page of the course list assuming
     * there are multiple pages.
     */
    const goBackToFirstPage = useCallback( () => {
        if ( paginationController && paginationController.setActivePage ) {
            // Reset the tracked page number in this component and in the pagination
            // controller component (tracked in two different places at the moment).
            paginationController.setActivePage( 0 );
            setActivePage( 0 );
        }
    }, [ paginationController, setActivePage ] );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const learningTypes = {
        curriculum: {
            label: <Translate.Text value='courseList.type.curriculum' />,
            getData: () => data.curricula,
        },
        course: {
            label: <Translate.Text value='courseList.type.course' />,
            getData: () => data.courses,
        }
};
    const [ learningType, setLearningType ] = useState( 'curriculum' );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const sortOptions = {
        alpha: {
            label: <><span><Translate.Text value='control.alphabetical' /></span> <CaretIcon direction='down' /></>,
            op: ( a, b ) => a.name.localeCompare( b.name )
        },
        antialpha: {
            label: <><span><Translate.Text value='control.antiAlphabetical' /></span> <CaretIcon direction='up' /></>,
            op: ( a, b ) => b.name.localeCompare( a.name )
        }
    };
    const [ sort, setSort ] = useState( 'alpha' );

    const viewOptions = {
        list: {
            label: <Translate.Text value='courseList.viewOptions.list.label' />,
        },
        card: {
            label: <Translate.Text value='courseList.viewOptions.grid.label' />,
        }
    };
    const [ view, setView ] = useState( 'card' );

    const loadingDataList = [ ...Array( itemsPerPage ) ].map( x => ( {} ) );
    
    const refinedDataList = useMemo( () => learningTypes[ learningType ].getData().sort( sortOptions[ sort ].op ), 
        [ learningTypes, learningType, sortOptions, sort ] );


    useEffect( () => {
        if ( !data || data.curricula == undefined || data.courses == undefined ) {
            return;
        }
        else if ( data.curricula.length > 0 ) {
            setLearningType( 'curriculum' );
        } else if ( data.courses.length > 0 ) {
            setLearningType( 'course' );
        }
    }, [ data.curricula, data.course ] );
    

    return (
        <div className={ classNames( 'learning-list', className ) } >
            <LoadingPopup visible={ loading } />

            { !loading &&  
            <>
                { /* Viewing controls */ }
                    <div className="learning-list-viewing-controls">

                        <div className='learningType-controls'>
                            {
                                Object.keys( learningTypes ).map( key => 
                                    <Button 
                                        key={ key }
                                        className={ classNames( key === learningType ? [ 'active' ] : [ 'inactive' ], 'control' ) }
                                        shape='round'
                                        onClick={ () => {setLearningType( key ); goBackToFirstPage();} }
                                        >
                                        { learningTypes[ key ].label }
                                    </Button> )
                            }
                        </div>

                        <div className='sort-controls'>
                            <Button
                                shape='round'
                                onClick={ () => {setSort( sort === 'alpha' ? 'antialpha' : 'alpha' ); goBackToFirstPage();} }
                                >
                                { sortOptions[ sort ].label }
                            </Button>
                        </div>

                        <div className='view-controls'>
                            {
                                Object.keys( viewOptions ).map( key => 
                                    <Button 
                                        key={ key }
                                        className={ classNames( key === view ? [ 'active' ] : [ 'inactive' ], 'control' ) }
                                        shape='round'
                                        onClick={ () => setView( key ) }
                                        >
                                        { viewOptions[ key ].label }
                                    </Button> )
                            }
                        </div>

                    </div>
                
                <LearningList
                    displayType={ view }
                    isLoading={ loading }
                    learningType={ learningType }
                    data={ loading ? loadingDataList : refinedDataList?.slice( activePage * itemsPerPage, ( activePage + 1 ) * itemsPerPage ) }
                />
                

                <div className="learning-list-pagination-controller">
                    <PaginationController
                        pageSize={ itemsPerPage }
                        itemCount={ !loading ? refinedDataList.length : 0 }
                        onChange={ handlePageChange }
                        getController={ setPaginationController }
                    />
                </div>
            </>
            }
        </div>
    );
};

/**
 * The default properties used for this component if values aren't given
 * to the component externally.
 */
 LearningListController.defaultProps = {
    className: '',

    data: {
        curricula: [],
        course: [],
    },
    loading: false,
};

/**
 * The type definitions of the properties this component accepts.
 */
 LearningListController.propTypes = {
    className: PropTypes.string,

    data: PropTypes.shape( {
        curricula: PropTypes.arrayOf( PropTypes.object ),
        course: PropTypes.arrayOf( PropTypes.object ),
    } ),
    loading: PropTypes.bool,
};
